import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "context/OnboardingContext";
import { onboardingService } from "services/onboarding-service";
import { prefillApi } from "services/onboarding/prefillApi";
import { useMaterialUIController } from "context";
import { getOnboardingThemeTokens, withAlpha } from "./themeTokens";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { motion, AnimatePresence } from "framer-motion";
import OnboardingLayout from "./OnboardingLayout";
import { CircularProgress, Divider, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PrefillConflictResolver from "../../components/onboarding/PrefillConflictResolver";
import DocumentUpload from "../../components/onboarding/DocumentUpload";

const steps = {
  entity: "entity_review",
  address: "address_review",
  contact: "contact_review",
  users: "users_review",
  subscriptions: "subscriptions_review",
  billing: "billing_review",
  attestation: "final_attestation",
};

const ReviewStep = ({ stepKey, title, fields }) => {
  const navigate = useNavigate();
  const { sessionToken, sessionId, theme, sessionData } = useOnboarding();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = getOnboardingThemeTokens({ darkMode, templateTheme: theme });
  const [data, setData] = useState({});
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolverOpen, setResolverOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    const fetchStepData = async () => {
      try {
        const res = await onboardingService.getSession(sessionId);
        const responseData = res.data || res;
        const stepState = responseData.state?.[stepKey];
        const actualData = stepState?.data ? stepState.data : stepState;
        setData(actualData || {});
        setStripeCustomerId(responseData.stripe_customer_id || "");
      } catch (err) {
        console.error("Error fetching session data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStepData();
  }, [sessionToken, stepKey]);

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      // Capture feedback for prefilled fields before confirming
      const decisions = [];
      const prefillMeta = sessionData?.prefill?.field_meta || {};
      
      Object.entries(data).forEach(([field, value]) => {
        const metaKey = `${steps[stepKey]}.${field}`;
        const meta = prefillMeta[metaKey];
        if (meta && meta.candidate_id) {
          decisions.push({
            candidate_id: meta.candidate_id,
            decision: value === meta.resolved_value_json ? 'accepted' : 'corrected',
            final_value: value,
            reason_code: 'user_confirmed'
          });
        }
      });

      if (decisions.length > 0) {
        await prefillApi.recordFeedback({
          sessionId: sessionData?.session_id,
          sessionToken,
          decisions
        }).catch(err => console.error("Feedback capture failed", err));
      }

      const resp = await onboardingService.saveStep(steps[stepKey], sessionId, {
        action: "confirm",
        data,
      });
      setStripeCustomerId(resp.data?.stripe_customer_id || stripeCustomerId);
      navigateStep(resp.data.next_step);
    } catch (err) {
      alert("Error confirming data");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async () => {
    setActionLoading(true);
    try {
      const resp = await onboardingService.saveStep(steps[stepKey], sessionId, {
        action: "edit",
        data,
      });
      setStripeCustomerId(resp.data?.stripe_customer_id || stripeCustomerId);
      setIsEditing(false);
    } catch (err) {
      alert("Error saving changes");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!data.email || !data.first_name) {
      alert("Please provide at least a name and email to create a billing profile.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await onboardingService.createCustomer(sessionId, {
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        email: data.email,
      });
      setStripeCustomerId(res.data.stripe_customer_id);
    } catch (err) {
      alert(err.response?.data?.message || "Error creating billing profile");
    } finally {
      setActionLoading(false);
    }
  };

  const navigateStep = (nextStep) => {
    const stepsList = [
      "entity_review",
      "address_review",
      "contact_review",
      "user_review",
    ];
    if (nextStep && stepsList.includes(nextStep)) {
      navigate(`/onboard/${nextStep}`);
    } else {
      navigate("/onboard/subscription_preview");
    }
  };

  if (loading && !data.name) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress color="info" />
        <MDTypography variant="button" sx={{ mt: 2 }}>
          Loading details...
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <OnboardingLayout>
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h3" fontWeight="bold">
            {title}
          </MDTypography>
          <MDBox display="flex" gap={1}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setUploadOpen(true)}
              sx={{ borderRadius: '12px', textTransform: 'none' }}
            >
              Upload Docs
            </Button>
            <Button 
              startIcon={<AutoAwesomeIcon />} 
              variant="outlined" 
              size="small" 
              onClick={() => setResolverOpen(true)}
              sx={{ borderRadius: '12px', textTransform: 'none' }}
            >
              Review Suggestions
            </Button>
          </MDBox>
        </MDBox>
        <MDTypography variant="body2" color="text" mb={4}>
          Please review the information below and confirm to proceed.
        </MDTypography>
      </motion.div>

      <MDBox mb={4}>
        <AnimatePresence mode="wait">
          <motion.div
            key={isEditing ? "editing" : "viewing"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {fields.map((field, index) => (
              <MDBox key={field.name} mb={2.5}>
                <motion.div
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="info"
                    textTransform="uppercase"
                    sx={{ display: "block", mb: 0.5, letterSpacing: "1px" }}
                  >
                    {field.label}
                  </MDTypography>
                  {isEditing ? (
                    <MDInput
                      value={data[field.name] || ""}
                      onChange={(e) => setData({ ...data, [field.name]: e.target.value })}
                      fullWidth
                      variant="standard"
                      sx={{ "& .MuiInput-root": { fontSize: "1rem" } }}
                    />
                  ) : (
                    <MDBox display="flex" alignItems="center">
                      <MDTypography variant="body1" fontWeight="medium">
                        {data[field.name] || (
                          <span style={{ opacity: 0.5, fontStyle: "italic", color: t.textMuted }}>Not provided</span>
                        )}
                      </MDTypography>
                      
                      {/* Prefill Indicator */}
                      {sessionData?.prefill?.field_meta?.[`${steps[stepKey]}.${field.name}`] && (
                        <Tooltip title={`Suggested from document (Confidence: ${Math.round(sessionData.prefill.field_meta[`${steps[stepKey]}.${field.name}`].confidence * 100)}%)`}>
                          <AutoAwesomeIcon 
                            sx={{ 
                              ml: 1, 
                              fontSize: 16, 
                              color: 'primary.main', 
                              cursor: 'help',
                              opacity: 0.7 
                            }} 
                          />
                        </Tooltip>
                      )}
                    </MDBox>
                  )}
                  {index < fields.length - 1 && !isEditing && (
                    <Divider sx={{ mt: 1.5, borderColor: t.divider }} />
                  )}
                </motion.div>
              </MDBox>
            ))}
          </motion.div>
        </AnimatePresence>
      </MDBox>

      <PrefillConflictResolver 
        open={resolverOpen} 
        onClose={() => setResolverOpen(false)} 
        sessionId={sessionData?.session_id} 
      />

      <Dialog 
        open={uploadOpen} 
        onClose={() => setUploadOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Upload Documents</DialogTitle>
        <DialogContent dividers>
          <DocumentUpload sessionId={sessionId} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Customer Creation Section for Contact Review */}
      {stepKey === "contact" && (
        <MDBox 
          mt={4} 
          p={3} 
          borderRadius="16px" 
          sx={{ 
            background: t.primarySoft,
            border: `1px dashed ${withAlpha(t.primary, darkMode ? 0.45 : 0.30)}`
          }}
        >
          <MDTypography variant="h6" fontWeight="bold" mb={1} color="info">
            Billing Profile
          </MDTypography>
          {stripeCustomerId ? (
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="body2" color="success">
                linked successfully
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                ID: {stripeCustomerId}
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              <MDTypography variant="body2" color="text" mb={2} sx={{ color: t.textMuted }}>
                A billing profile is required to manage your subscriptions. Create one now using the details above.
              </MDTypography>
              <MDButton 
                variant="outlined" 
                color="info" 
                size="small" 
                onClick={handleCreateCustomer}
                disabled={actionLoading || isEditing}
              >
                {actionLoading ? "Linking..." : "Link Billing Profile"}
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      )}

      <MDBox display="flex" justifyContent="flex-end" mt={5} gap={2}>
        <AnimatePresence mode="wait">
          {!isEditing ? (
            <motion.div
              key="view-btns"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: "flex", gap: "12px", width: "100%" }}
            >
              <MDButton
                variant="outlined"
                color="info"
                onClick={() => setIsEditing(true)}
                disabled={actionLoading}
                sx={{ flex: 1, borderRadius: "12px" }}
              >
                Edit Details
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleConfirm}
                disabled={actionLoading || (stepKey === "contact" && !stripeCustomerId)}
                sx={{ flex: 2, borderRadius: "12px" }}
              >
                {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Confirm & Continue"}
              </MDButton>
            </motion.div>
          ) : (
            <motion.div
              key="edit-btns"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: "flex", gap: "12px", width: "100%" }}
            >
              <MDButton
                variant="text"
                color="secondary"
                onClick={() => setIsEditing(false)}
                disabled={actionLoading}
                sx={{ flex: 1, borderRadius: "12px" }}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleSave}
                disabled={actionLoading}
                sx={{ flex: 2, borderRadius: "12px" }}
              >
                {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
              </MDButton>
            </motion.div>
          )}
        </AnimatePresence>
      </MDBox>
    </OnboardingLayout>
  );
};

export default ReviewStep;
